import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Bell, ScanLine, CheckCircle2, Receipt } from "lucide-react";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = items.filter(n => !n.is_read).length;

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/me", { params: { unread_only: false } });
      setItems(data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((items) => items.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch { /* silent */ }
  };

  const fmt = (iso) => iso?.slice(0, 16).replace("T", " ");

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell"
        className="relative h-9 w-9 rounded-md hover:bg-slate-800 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#C5A059] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-96 max-h-[70vh] overflow-y-auto bg-white text-slate-800 rounded-lg shadow-xl border border-slate-200 z-40" data-testid="notif-panel">
            <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
              <div className="font-semibold text-sm">Alertes & notifications</div>
              <span className="text-xs text-slate-500">{unread} non lue{unread > 1 ? "s" : ""}</span>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-slate-500">Aucune notification.</div>
            ) : (
              <div>
                {items.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => !n.is_read && markRead(n.id)}
                    className={`px-4 py-3 border-b border-slate-100 cursor-pointer transition-colors ${n.is_read ? "bg-white" : "bg-[#FDF9F1] hover:bg-[#FBEFD5]"}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${n.kind === "qr_alert" ? "bg-[#C5A059]/20 text-[#A88846]" : n.kind === "auto_facture" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {n.kind === "qr_alert" ? <ScanLine className="h-3.5 w-3.5" /> : n.kind === "auto_facture" ? <Receipt className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-slate-900 truncate">{n.subject}</div>
                        <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{fmt(n.created_at)}</div>
                      </div>
                      {n.is_read && <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="px-4 py-2 text-center border-t border-slate-100">
              <a href="/app/qr-scans" className="text-xs text-[#C5A059] hover:text-[#A88846] font-medium">Voir le journal des scans →</a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
